import type { fabric } from 'fabric';
import type { FabricEditor } from 'modules/fabric-editor';
import { DEFAULT_IMAGE_CORNER_RADIUS } from 'modules/manager/domain/placeholder/placeholder-factory';
import type { PlaceholderCornerRadii, PlaceholderImageData } from 'modules/manager/domain/placeholder/types';
import type { CornerRadiiStrategy } from './use-corner-radii';

/**
 * Image-placeholder corner radii: uniform in `data.cornerRadius` (default
 * `DEFAULT_IMAGE_CORNER_RADIUS`), per-corner in `data.cornerRadii`. Each write
 * refits the placeholder group so the regenerated `fabric.Path` / `fabric.Rect`
 * visual matches the new `data` (the single source of truth for the visual).
 */
export function imagePlaceholderCornerStrategy(editor: FabricEditor): CornerRadiiStrategy {
  const dataOf = (o: fabric.Object) => o.data as PlaceholderImageData | undefined;
  return {
    readUniform: (o) => dataOf(o)?.cornerRadius ?? DEFAULT_IMAGE_CORNER_RADIUS,
    readPerCorner: (o) => dataOf(o)?.cornerRadii,
    writeUniform: (o, value) => {
      editor.objects.setData(o, 'cornerRadius', value);
      editor.objects.setData(o, 'cornerRadii', undefined);
      editor.placeholders?.refit(o as fabric.Group);
    },
    writePerCorner: (o, radii) => {
      editor.objects.setData(o, 'cornerRadii', radii);
      editor.placeholders?.refit(o as fabric.Group);
    },
  };
}

/**
 * Native-rect corner radii: the uniform value lives in the `rx`/`ry` props (stock
 * Fabric rounding), per-corner in `data.cornerRadii` drawn by the rect-corners
 * plugin. Per-corner writes route through `editor.rectCorners`, which marks the
 * cached rect dirty and repaints — a bare `setData` would leave the new geometry
 * invisible until an unrelated redraw. `writeUniform` clears `data.cornerRadii`
 * (also via the façade) so the rect returns to the stock uniform render.
 */
export function rectCornerStrategy(editor: FabricEditor): CornerRadiiStrategy {
  const radiiOf = (o: fabric.Object) =>
    (o.data as { cornerRadii?: PlaceholderCornerRadii } | undefined)?.cornerRadii;
  return {
    readUniform: (o) => (o as fabric.Rect).rx ?? 0,
    readPerCorner: (o) => radiiOf(o),
    writeUniform: (o, value) => {
      editor.objects.setProp(o, 'rx' as keyof fabric.Object, value as never);
      editor.objects.setProp(o, 'ry' as keyof fabric.Object, value as never);
      editor.rectCorners?.set(o as fabric.Rect, undefined);
    },
    writePerCorner: (o, radii) => {
      editor.rectCorners?.set(o as fabric.Rect, radii);
    },
  };
}
